import React, { useEffect, useState, useCallback } from "react";
import { usePagesVisibility } from "../context/PagesContext";
import {
  Save, Eye, EyeOff, RotateCcw, Plus, Trash2, ChevronDown, LayoutGrid,
} from "lucide-react";
import api, { describeAxiosError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "./ui/accordion";

const EMPTY_INDICATOR = { value: 0, decimals: 0, suffix: "", prefix: "", label_es: "", label_en: "", visible: true };
const EMPTY_INDUSTRY = { count: "", title_es: "", title_en: "", desc_es: "", desc_en: "", visible: true };
const EMPTY_INSIGHT = { type: "white-paper", date: "", title_es: "", title_en: "", summary_es: "", summary_en: "", link: "", visible: true };

const PAGE_KEYS = [
  { key: "home", label_es: "Inicio (Home)", label_en: "Home", note: "siempre visible — protegida" },
  { key: "about", label_es: "Quiénes Somos", label_en: "About Us" },
  { key: "services", label_es: "Servicios y Soluciones", label_en: "Services & Solutions" },
  { key: "iieas", label_es: "Instituto IIEAS", label_en: "IIEAS Institute" },
  { key: "synapse", label_es: "Convergen Synapse", label_en: "Convergen Synapse" },
  { key: "insights", label_es: "Insights (archivo público)", label_en: "Insights (public archive)" },
  { key: "contact", label_es: "Contacto", label_en: "Contact" },
];

export default function ContentEditor() {
  const { refresh: refreshPages } = usePagesVisibility();
  const [content, setContent] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/content");
      setContent(data);
      setOriginal(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        setError("Tu sesión expiró o no tienes permisos. Vuelve a iniciar sesión.");
        setTimeout(() => { window.location.href = "/admin/login"; }, 1800);
      } else {
        setError(`No se pudo cargar el contenido: ${describeAxiosError(e)}`);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(content) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put("/admin/content", content);
      setContent(data);
      setOriginal(JSON.parse(JSON.stringify(data)));
      setSavedAt(new Date());
      // Sync nav visibility immediately
      refreshPages();
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        setError("Tu sesión expiró. Por favor vuelve a iniciar sesión para guardar los cambios.");
        // Send back to login after a brief moment so the user sees the message
        setTimeout(() => { window.location.href = "/admin/login"; }, 1800);
      } else if (status === 422) {
        const detail = e.response?.data?.detail;
        setError(`No se pudo guardar: ${Array.isArray(detail) ? detail.map(x => x.msg || JSON.stringify(x)).join(" · ") : (detail || "datos inválidos")}`);
      } else if (status === 0 || !status) {
        setError("Sin conexión al servidor. Verifica tu red e inténtalo de nuevo.");
      } else {
        setError(`Error al guardar (HTTP ${status}). Intenta de nuevo.`);
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setContent(JSON.parse(JSON.stringify(original)));

  // Generic setter for top-level paths
  const setPath = useCallback((path, value) => {
    setContent((c) => {
      const next = JSON.parse(JSON.stringify(c));
      const segs = path.split(".");
      let obj = next;
      for (let i = 0; i < segs.length - 1; i++) {
        const k = segs[i];
        const isIndex = /^\d+$/.test(k);
        obj = obj[isIndex ? Number(k) : k];
      }
      obj[segs[segs.length - 1]] = value;
      return next;
    });
  }, []);

  // Array list helpers
  const addItem = (key, blank) => setContent((c) => ({ ...c, [key]: [...(c[key] || []), { ...blank }] }));
  const delItem = (key, idx) => setContent((c) => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) }));
  const toggleVis = (key, idx) => setContent((c) => ({
    ...c,
    [key]: c[key].map((it, i) => i === idx ? { ...it, visible: !it.visible } : it),
  }));

  if (!content) {
    return (
      <div className="space-y-4" data-testid="content-editor-loading">
        {error ? (
          <>
            <div className="bg-red-50 border border-red-300 text-red-800 px-5 py-4 text-sm" data-testid="cms-load-error">
              {error}
            </div>
            <Button
              onClick={load}
              data-testid="cms-load-retry"
              className="bg-cerulean hover:bg-ink text-white rounded-none h-10 px-5 text-[12px] tracking-[0.18em] uppercase"
            >
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} /> Reintentar
            </Button>
          </>
        ) : (
          <div className="text-sm text-text-muted">Cargando…</div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="content-editor" className="space-y-8">
      {/* Sticky toolbar */}
      <div className="sticky top-16 z-20 -mx-6 px-6 py-4 bg-pearl/95 backdrop-blur border-b border-rule flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-text-muted">CMS · HOME PAGE</div>
          <div className="text-sm text-ink mt-1">
            {dirty ? "Cambios sin guardar" : (savedAt ? `Guardado ${savedAt.toLocaleTimeString()}` : "Sin cambios")}
          </div>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <Button data-testid="cms-reset-btn" variant="outline" onClick={reset} className="rounded-none border-ink h-10 text-[12px] tracking-[0.18em] uppercase">
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} /> Descartar
            </Button>
          )}
          <Button data-testid="cms-save-btn" onClick={save} disabled={!dirty || saving}
            className="bg-cerulean hover:bg-ink text-white rounded-none h-10 px-5 text-[12px] tracking-[0.18em] uppercase disabled:opacity-40">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-800 px-5 py-4 text-sm" data-testid="cms-error">
          {error}
        </div>
      )}

      <Accordion type="multiple" defaultValue={["pages", "hero", "indicators"]} className="space-y-3">
        {/* ---------- PAGES VISIBILITY ---------- */}
        <Section id="pages" idx="000" title="Visibilidad de páginas" desc="Activa o desactiva páginas completas del sitio público. Las páginas ocultas no aparecen en el menú y redirigen al Home.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PAGE_KEYS.map((p) => {
              const isHome = p.key === "home";
              const value = content.pages?.[p.key] !== false;
              return (
                <div key={p.key} className="flex items-center justify-between bg-pearl border border-rule p-4" data-testid={`pages-row-${p.key}`}>
                  <div className="flex items-center gap-3">
                    <LayoutGrid className="w-4 h-4 text-cerulean" strokeWidth={1.5} />
                    <div>
                      <div className="text-sm text-ink">{p.label_es}</div>
                      <div className="text-[10px] font-mono tracking-widest text-text-muted">/{p.key === "home" ? "" : p.key === "about" ? "quienes-somos" : p.key === "services" ? "servicios" : p.key === "contact" ? "contacto" : p.key} {p.note ? `· ${p.note}` : ""}</div>
                    </div>
                  </div>
                  <Switch
                    checked={value}
                    disabled={isHome}
                    onCheckedChange={(checked) => setPath(`pages.${p.key}`, !!checked)}
                    data-testid={`pages-toggle-${p.key}`}
                  />
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-[11px] font-mono tracking-[0.18em] uppercase text-text-muted">
            Cambios surten efecto inmediato al guardar.
          </p>
        </Section>

        {/* ---------- HERO ---------- */}
        <Section id="hero" idx="001" title="Hero principal" desc="Eslogan superior, títulos, subtítulo y CTAs.">
          <Grid>
            <Field label="Eyebrow (ES)" cols="md:col-span-6"><Input value={content.hero.eyebrow_es} onChange={(e) => setPath("hero.eyebrow_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Eyebrow (EN)" cols="md:col-span-6"><Input value={content.hero.eyebrow_en} onChange={(e) => setPath("hero.eyebrow_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Título línea 1 (ES)" cols="md:col-span-6"><Input value={content.hero.title1_es} onChange={(e) => setPath("hero.title1_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Título línea 1 (EN)" cols="md:col-span-6"><Input value={content.hero.title1_en} onChange={(e) => setPath("hero.title1_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Título línea 2 / acento (ES)" cols="md:col-span-6"><Input value={content.hero.title2_es} onChange={(e) => setPath("hero.title2_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Título línea 2 / acento (EN)" cols="md:col-span-6"><Input value={content.hero.title2_en} onChange={(e) => setPath("hero.title2_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Subtítulo (ES)" cols="md:col-span-6"><Textarea rows={3} value={content.hero.sub_es} onChange={(e) => setPath("hero.sub_es", e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
            <Field label="Subtítulo (EN)" cols="md:col-span-6"><Textarea rows={3} value={content.hero.sub_en} onChange={(e) => setPath("hero.sub_en", e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
            <Field label="CTA primario (ES)" cols="md:col-span-3"><Input value={content.hero.cta_primary_es} onChange={(e) => setPath("hero.cta_primary_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="CTA primario (EN)" cols="md:col-span-3"><Input value={content.hero.cta_primary_en} onChange={(e) => setPath("hero.cta_primary_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="CTA secundario (ES)" cols="md:col-span-3"><Input value={content.hero.cta_secondary_es} onChange={(e) => setPath("hero.cta_secondary_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="CTA secundario (EN)" cols="md:col-span-3"><Input value={content.hero.cta_secondary_en} onChange={(e) => setPath("hero.cta_secondary_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
          </Grid>
        </Section>

        {/* ---------- CHALLENGE ---------- */}
        <Section id="challenge" idx="002" title="Sección · El Desafío" desc="Eyebrow, título principal, párrafo 1 y párrafo 2.">
          <HeaderFields prefix="challenge" content={content} setPath={setPath} />
          <Grid>
            <Field label="Párrafo extra (ES)" cols="md:col-span-6"><Textarea rows={4} value={content.challenge_extra_es} onChange={(e) => setPath("challenge_extra_es", e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
            <Field label="Párrafo extra (EN)" cols="md:col-span-6"><Textarea rows={4} value={content.challenge_extra_en} onChange={(e) => setPath("challenge_extra_en", e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
          </Grid>
        </Section>

        {/* ---------- EVOLUTION ---------- */}
        <Section id="evolution" idx="003" title="Sección · Evolución Estratégica" desc="Bloque “De / A”.">
          <HeaderFields prefix="evolution" content={content} setPath={setPath} />
          <Grid>
            <Field label="Etiqueta DE (ES)" cols="md:col-span-3"><Input value={content.evolution_from_es} onChange={(e) => setPath("evolution_from_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Etiqueta DE (EN)" cols="md:col-span-3"><Input value={content.evolution_from_en} onChange={(e) => setPath("evolution_from_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Etiqueta A (ES)" cols="md:col-span-3"><Input value={content.evolution_to_es} onChange={(e) => setPath("evolution_to_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Etiqueta A (EN)" cols="md:col-span-3"><Input value={content.evolution_to_en} onChange={(e) => setPath("evolution_to_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
          </Grid>
        </Section>

        {/* ---------- MOSAIC ---------- */}
        <Section id="mosaic" idx="004" title="Sección · Mosaico de Ramas" desc="Eyebrow + título de la sección y los 3 cards (Consultoría / IIEAS / Synapse).">
          <HeaderFields prefix="mosaic_section" content={content} setPath={setPath} hideBody />
          <div className="mt-5 space-y-4">
            {content.mosaic.map((it, idx) => (
              <Card key={idx} index={idx} prefix={["CONSULTORÍA", "IIEAS", "SYNAPSE"][idx] || `Rama ${idx + 1}`}>
                <Grid>
                  <Field label="Título (ES)" cols="md:col-span-6"><Input value={it.title_es} onChange={(e) => setPath(`mosaic.${idx}.title_es`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Título (EN)" cols="md:col-span-6"><Input value={it.title_en} onChange={(e) => setPath(`mosaic.${idx}.title_en`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Descripción (ES)" cols="md:col-span-6"><Textarea rows={2} value={it.desc_es} onChange={(e) => setPath(`mosaic.${idx}.desc_es`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
                  <Field label="Descripción (EN)" cols="md:col-span-6"><Textarea rows={2} value={it.desc_en} onChange={(e) => setPath(`mosaic.${idx}.desc_en`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
                </Grid>
              </Card>
            ))}
          </div>
        </Section>

        {/* ---------- WHITE PAPER (lead capture en KPI vacíos) ---------- */}
        <Section
          id="whitepaper"
          idx="004B"
          title="Sección · Captura White Paper (Home)"
          desc="Formulario que aparece en la sección de KPIs cuando aún no se han cargado indicadores. Permite captar suscriptores antes de publicar métricas."
        >
          <div className="bg-pearl border border-rule p-4 flex items-center justify-between mb-5">
            <div>
              <div className="text-sm text-ink">Mostrar formulario en Home</div>
              <div className="text-[10px] font-mono tracking-widest text-text-muted mt-1">
                Si lo desactivas, el bloque desaparece aunque no haya KPIs.
              </div>
            </div>
            <Switch
              checked={content.whitepaper?.enabled !== false}
              onCheckedChange={(checked) => setPath("whitepaper.enabled", !!checked)}
              data-testid="wp-enabled-toggle"
            />
          </div>
          <Grid>
            <Field label="Eyebrow / etiqueta superior (ES)" cols="md:col-span-6">
              <Input data-testid="wp-eyebrow-es" value={content.whitepaper?.eyebrow_es || ""} onChange={(e) => setPath("whitepaper.eyebrow_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
            <Field label="Eyebrow / label (EN)" cols="md:col-span-6">
              <Input value={content.whitepaper?.eyebrow_en || ""} onChange={(e) => setPath("whitepaper.eyebrow_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
            <Field label="Título (ES)" cols="md:col-span-6">
              <Input data-testid="wp-title-es" value={content.whitepaper?.title_es || ""} onChange={(e) => setPath("whitepaper.title_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
            <Field label="Title (EN)" cols="md:col-span-6">
              <Input value={content.whitepaper?.title_en || ""} onChange={(e) => setPath("whitepaper.title_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
            <Field label="Descripción (ES)" cols="md:col-span-6">
              <Textarea rows={3} data-testid="wp-desc-es" value={content.whitepaper?.description_es || ""} onChange={(e) => setPath("whitepaper.description_es", e.target.value)} className="rounded-none border-rule focus:border-cerulean" />
            </Field>
            <Field label="Description (EN)" cols="md:col-span-6">
              <Textarea rows={3} value={content.whitepaper?.description_en || ""} onChange={(e) => setPath("whitepaper.description_en", e.target.value)} className="rounded-none border-rule focus:border-cerulean" />
            </Field>
            <Field label="Fecha informativa (opcional)" cols="md:col-span-4">
              <Input placeholder="Feb 2026" value={content.whitepaper?.date || ""} onChange={(e) => setPath("whitepaper.date", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
            <Field label="Texto del botón (ES)" cols="md:col-span-4">
              <Input data-testid="wp-cta-es" value={content.whitepaper?.cta_es || ""} onChange={(e) => setPath("whitepaper.cta_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
            <Field label="Button label (EN)" cols="md:col-span-4">
              <Input value={content.whitepaper?.cta_en || ""} onChange={(e) => setPath("whitepaper.cta_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" />
            </Field>
          </Grid>
          <p className="mt-4 text-[11px] font-mono tracking-[0.18em] uppercase text-text-muted">
            Los envíos llegan al panel de Leads con la dimensión “White Paper · IIEAS”.
          </p>
        </Section>

        {/* ---------- INDICATORS ---------- */}
        <Section id="indicators" idx="005" title="Sección · Indicadores (KPI)" desc="Header, métricas animadas y disclaimer.">
          <HeaderFields prefix="indicators_section" content={content} setPath={setPath} />
          <Grid>
            <Field label="Disclaimer (ES)" cols="md:col-span-6"><Input value={content.indicators_disclaimer_es} onChange={(e) => setPath("indicators_disclaimer_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Disclaimer (EN)" cols="md:col-span-6"><Input value={content.indicators_disclaimer_en} onChange={(e) => setPath("indicators_disclaimer_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
          </Grid>
          <div className="mt-6 flex justify-end">
            <Button data-testid="add-indicator-btn" variant="outline" onClick={() => addItem("indicators", EMPTY_INDICATOR)} className="rounded-none h-9 border-ink text-[11px] tracking-[0.18em] uppercase">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Añadir KPI
            </Button>
          </div>
          <div className="mt-4 space-y-4">
            {content.indicators.map((it, idx) => (
              <Card key={idx} index={idx} prefix="KPI">
                <Grid>
                  <Field label="Valor" cols="col-span-6 md:col-span-2"><Input data-testid={`ind-value-${idx}`} type="number" step="any" value={it.value} onChange={(e) => setPath(`indicators.${idx}.value`, parseFloat(e.target.value) || 0)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Decimales" cols="col-span-6 md:col-span-1"><Input type="number" min="0" max="3" value={it.decimals} onChange={(e) => setPath(`indicators.${idx}.decimals`, parseInt(e.target.value || 0, 10))} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Prefijo" cols="col-span-6 md:col-span-1"><Input value={it.prefix || ""} onChange={(e) => setPath(`indicators.${idx}.prefix`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Sufijo" cols="col-span-6 md:col-span-2"><Input value={it.suffix || ""} placeholder='M, %, +' onChange={(e) => setPath(`indicators.${idx}.suffix`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Etiqueta (ES)" cols="col-span-12 md:col-span-3"><Input data-testid={`ind-label-es-${idx}`} value={it.label_es} onChange={(e) => setPath(`indicators.${idx}.label_es`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Etiqueta (EN)" cols="col-span-12 md:col-span-3"><Input value={it.label_en} onChange={(e) => setPath(`indicators.${idx}.label_en`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                </Grid>
                <Actions
                  visible={it.visible}
                  onToggle={() => toggleVis("indicators", idx)}
                  onDelete={() => delItem("indicators", idx)}
                  testidPrefix={`ind-${idx}`}
                />
              </Card>
            ))}
          </div>
        </Section>

        {/* ---------- INDUSTRIES ---------- */}
        <Section id="industries" idx="006" title="Sección · Organizaciones / Sectores" desc="Header + nota + tarjetas de sectores.">
          <HeaderFields prefix="industries_section" content={content} setPath={setPath} />
          <Grid>
            <Field label="Nota lateral (ES)" cols="md:col-span-6"><Input value={content.industries_note_es} onChange={(e) => setPath("industries_note_es", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
            <Field label="Nota lateral (EN)" cols="md:col-span-6"><Input value={content.industries_note_en} onChange={(e) => setPath("industries_note_en", e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
          </Grid>
          <div className="mt-6 flex justify-end">
            <Button data-testid="add-industry-btn" variant="outline" onClick={() => addItem("industries", EMPTY_INDUSTRY)} className="rounded-none h-9 border-ink text-[11px] tracking-[0.18em] uppercase">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Añadir sector
            </Button>
          </div>
          <div className="mt-4 space-y-4">
            {content.industries.map((it, idx) => (
              <Card key={idx} index={idx} prefix="SEG">
                <Grid>
                  <Field label="Conteo" cols="col-span-12 md:col-span-2"><Input value={it.count} placeholder="10+, 05, etc." onChange={(e) => setPath(`industries.${idx}.count`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Título (ES)" cols="col-span-12 md:col-span-5"><Input value={it.title_es} onChange={(e) => setPath(`industries.${idx}.title_es`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Título (EN)" cols="col-span-12 md:col-span-5"><Input value={it.title_en} onChange={(e) => setPath(`industries.${idx}.title_en`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Descripción (ES)" cols="col-span-12 md:col-span-6"><Textarea rows={2} value={it.desc_es} onChange={(e) => setPath(`industries.${idx}.desc_es`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
                  <Field label="Descripción (EN)" cols="col-span-12 md:col-span-6"><Textarea rows={2} value={it.desc_en} onChange={(e) => setPath(`industries.${idx}.desc_en`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
                </Grid>
                <Actions
                  visible={it.visible}
                  onToggle={() => toggleVis("industries", idx)}
                  onDelete={() => delItem("industries", idx)}
                  testidPrefix={`ind2-${idx}`}
                />
              </Card>
            ))}
          </div>
        </Section>

        {/* ---------- INSIGHTS / PENSAMIENTO ---------- */}
        <Section id="insights" idx="007" title="Sección · Pensamiento Convergen (Insights)" desc="White papers, comunicados, consensos y casos. Si el array queda vacío, la sección no aparece en el sitio.">
          <HeaderFields prefix="insights_section" content={content} setPath={setPath} />
          <div className="mt-6 flex justify-end">
            <Button data-testid="add-insight-btn" variant="outline" onClick={() => addItem("insights", EMPTY_INSIGHT)} className="rounded-none h-9 border-ink text-[11px] tracking-[0.18em] uppercase">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Añadir publicación
            </Button>
          </div>
          <div className="mt-4 space-y-4">
            {content.insights.map((it, idx) => (
              <Card key={idx} index={idx} prefix="PUB">
                <Grid>
                  <Field label="Tipo" cols="col-span-6 md:col-span-2">
                    <select
                      data-testid={`ins-type-${idx}`}
                      value={it.type}
                      onChange={(e) => setPath(`insights.${idx}.type`, e.target.value)}
                      className="w-full h-10 px-3 border border-rule focus:border-cerulean text-sm bg-white rounded-none focus:outline-none"
                    >
                      <option value="white-paper">White Paper</option>
                      <option value="press">Comunicado</option>
                      <option value="consensus">Consenso</option>
                      <option value="case">Caso</option>
                    </select>
                  </Field>
                  <Field label="Fecha" cols="col-span-6 md:col-span-2"><Input data-testid={`ins-date-${idx}`} value={it.date} placeholder="Feb 2026" onChange={(e) => setPath(`insights.${idx}.date`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Enlace externo (opcional)" cols="col-span-12 md:col-span-8"><Input data-testid={`ins-link-${idx}`} value={it.link} placeholder="https://…" onChange={(e) => setPath(`insights.${idx}.link`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Título (ES)" cols="col-span-12 md:col-span-6"><Input data-testid={`ins-title-es-${idx}`} value={it.title_es} onChange={(e) => setPath(`insights.${idx}.title_es`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Título (EN)" cols="col-span-12 md:col-span-6"><Input value={it.title_en} onChange={(e) => setPath(`insights.${idx}.title_en`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
                  <Field label="Resumen (ES)" cols="col-span-12 md:col-span-6"><Textarea rows={3} value={it.summary_es} onChange={(e) => setPath(`insights.${idx}.summary_es`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
                  <Field label="Resumen (EN)" cols="col-span-12 md:col-span-6"><Textarea rows={3} value={it.summary_en} onChange={(e) => setPath(`insights.${idx}.summary_en`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
                </Grid>
                <Actions
                  visible={it.visible}
                  onToggle={() => toggleVis("insights", idx)}
                  onDelete={() => delItem("insights", idx)}
                  testidPrefix={`ins-${idx}`}
                />
              </Card>
            ))}
          </div>
        </Section>
      </Accordion>
    </div>
  );
}

/* ---------- helpers ---------- */
function Section({ id, idx, title, desc, children }) {
  return (
    <AccordionItem value={id} className="bg-white border border-rule">
      <AccordionTrigger className="px-6 py-5 hover:no-underline" data-testid={`section-${id}-toggle`}>
        <div className="flex items-center gap-4 text-left">
          <span className="text-[10px] font-mono tracking-[0.22em] text-cerulean">{idx}</span>
          <div>
            <div className="display-heading text-base md:text-lg">{title}</div>
            {desc && <div className="text-xs text-text-muted mt-1 font-normal">{desc}</div>}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-6 pb-6">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

function HeaderFields({ prefix, content, setPath, hideBody = false }) {
  const obj = content[prefix] || {};
  return (
    <Grid>
      <Field label="Eyebrow (ES)" cols="md:col-span-3"><Input value={obj.eyebrow_es || ""} onChange={(e) => setPath(`${prefix}.eyebrow_es`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
      <Field label="Eyebrow (EN)" cols="md:col-span-3"><Input value={obj.eyebrow_en || ""} onChange={(e) => setPath(`${prefix}.eyebrow_en`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
      <Field label="Título (ES)" cols="md:col-span-3"><Input value={obj.title_es || ""} onChange={(e) => setPath(`${prefix}.title_es`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
      <Field label="Título (EN)" cols="md:col-span-3"><Input value={obj.title_en || ""} onChange={(e) => setPath(`${prefix}.title_en`, e.target.value)} className="rounded-none h-10 border-rule focus:border-cerulean" /></Field>
      {!hideBody && (
        <>
          <Field label="Texto descriptivo (ES)" cols="md:col-span-6"><Textarea rows={3} value={obj.body_es || ""} onChange={(e) => setPath(`${prefix}.body_es`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
          <Field label="Texto descriptivo (EN)" cols="md:col-span-6"><Textarea rows={3} value={obj.body_en || ""} onChange={(e) => setPath(`${prefix}.body_en`, e.target.value)} className="rounded-none border-rule focus:border-cerulean" /></Field>
        </>
      )}
    </Grid>
  );
}

function Card({ index, prefix, children }) {
  return (
    <div className="bg-pearl border border-rule p-6">
      <div className="text-[10px] font-mono tracking-[0.22em] text-cerulean mb-4">{prefix} {String(index + 1).padStart(2, "0")}</div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-12 gap-3">{children}</div>;
}

function Field({ label, cols = "col-span-12", children }) {
  return (
    <label className={`block ${cols}`}>
      <span className="block text-[10px] font-mono tracking-[0.18em] uppercase text-text-muted mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Actions({ visible, onToggle, onDelete, testidPrefix }) {
  return (
    <div className="mt-5 pt-4 border-t border-rule flex items-center justify-between">
      <button
        data-testid={`${testidPrefix}-toggle`}
        onClick={onToggle}
        className={`flex items-center gap-2 text-[11px] font-mono tracking-[0.18em] uppercase transition-colors ${visible ? "text-cerulean" : "text-text-muted"}`}
      >
        {visible ? <Eye className="w-4 h-4" strokeWidth={1.5} /> : <EyeOff className="w-4 h-4" strokeWidth={1.5} />}
        {visible ? "Visible" : "Oculto"}
      </button>
      <button
        data-testid={`${testidPrefix}-delete`}
        onClick={onDelete}
        className="text-red-500 hover:text-red-700 text-[11px] font-mono tracking-[0.18em] uppercase flex items-center gap-2"
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Eliminar
      </button>
    </div>
  );
}
